import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";

/**
 * STLViewer: minimal, dependency-light STL parser + three.js renderer.
 * Supports both binary and ASCII STL.
 * Returns analysis (volume mm³, weight g @ density, bounding box, triangle count).
 */

// -------- STL Parser --------
export function parseSTL(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  // Detect ASCII vs binary. Binary has 80-byte header + uint32 triangle count.
  const isBinary = () => {
    if (arrayBuffer.byteLength < 84) return false;
    const nTri = view.getUint32(80, true);
    const expected = 84 + nTri * 50;
    return expected === arrayBuffer.byteLength;
  };
  if (isBinary()) return parseBinarySTL(view);
  const text = new TextDecoder().decode(arrayBuffer);
  return parseASCIISTL(text);
}

function parseBinarySTL(view) {
  const nTri = view.getUint32(80, true);
  const positions = new Float32Array(nTri * 9);
  const normals = new Float32Array(nTri * 9);
  let offset = 84;
  for (let i = 0; i < nTri; i++) {
    const nx = view.getFloat32(offset, true);
    const ny = view.getFloat32(offset + 4, true);
    const nz = view.getFloat32(offset + 8, true);
    offset += 12;
    for (let v = 0; v < 3; v++) {
      const px = i * 9 + v * 3;
      positions[px] = view.getFloat32(offset, true);
      positions[px + 1] = view.getFloat32(offset + 4, true);
      positions[px + 2] = view.getFloat32(offset + 8, true);
      normals[px] = nx; normals[px + 1] = ny; normals[px + 2] = nz;
      offset += 12;
    }
    offset += 2; // attribute byte count
  }
  return { positions, normals, triangleCount: nTri };
}

function parseASCIISTL(text) {
  const positions = [];
  const normals = [];
  const nRegex = /facet normal ([-0-9.eE+]+) ([-0-9.eE+]+) ([-0-9.eE+]+)/g;
  const vRegex = /vertex ([-0-9.eE+]+) ([-0-9.eE+]+) ([-0-9.eE+]+)/g;
  const facets = text.split("facet normal");
  let nTri = 0;
  for (let i = 1; i < facets.length; i++) {
    const facet = "facet normal" + facets[i];
    nRegex.lastIndex = 0; vRegex.lastIndex = 0;
    const nm = nRegex.exec(facet);
    if (!nm) continue;
    const nx = +nm[1], ny = +nm[2], nz = +nm[3];
    let m; const verts = [];
    while ((m = vRegex.exec(facet)) && verts.length < 3) {
      verts.push([+m[1], +m[2], +m[3]]);
    }
    if (verts.length === 3) {
      for (const v of verts) {
        positions.push(v[0], v[1], v[2]);
        normals.push(nx, ny, nz);
      }
      nTri++;
    }
  }
  return { positions: new Float32Array(positions), normals: new Float32Array(normals), triangleCount: nTri };
}

// -------- Analysis --------
export function analyzeGeometry(positions, densityGcm3 = 1.24) {
  // Compute signed volume via divergence theorem (in mm³)
  let vol = 0;
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const n = positions.length / 9;
  for (let i = 0; i < n; i++) {
    const a = i * 9;
    const x1 = positions[a], y1 = positions[a + 1], z1 = positions[a + 2];
    const x2 = positions[a + 3], y2 = positions[a + 4], z2 = positions[a + 5];
    const x3 = positions[a + 6], y3 = positions[a + 7], z3 = positions[a + 8];
    vol += (
      x1 * (y2 * z3 - y3 * z2) +
      x2 * (y3 * z1 - y1 * z3) +
      x3 * (y1 * z2 - y2 * z1)
    ) / 6;
    for (const [x, y, z] of [[x1, y1, z1], [x2, y2, z2], [x3, y3, z3]]) {
      if (x < minX) minX = x; if (y < minY) minY = y; if (z < minZ) minZ = z;
      if (x > maxX) maxX = x; if (y > maxY) maxY = y; if (z > maxZ) maxZ = z;
    }
  }
  const volumeMm3 = Math.abs(vol);
  const volumeCm3 = volumeMm3 / 1000;
  const weightG = volumeCm3 * densityGcm3;
  return {
    volumeMm3, volumeCm3, weightG,
    bbox: {
      x: maxX - minX, y: maxY - minY, z: maxZ - minZ,
      minX, minY, minZ, maxX, maxY, maxZ,
    },
    triangleCount: n,
  };
}

// -------- Viewer Component --------
export default function STLViewer({ arrayBuffer, onAnalysis, height = 400 }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!arrayBuffer || !containerRef.current) return;
    try {
      const { positions, normals, triangleCount } = parseSTL(arrayBuffer);
      const analysis = analyzeGeometry(positions);
      onAnalysis && onAnalysis({ ...analysis, triangleCount });

      const container = containerRef.current;
      const width = container.clientWidth;
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf9f9fb);

      const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(width, height);
      container.innerHTML = "";
      container.appendChild(renderer.domElement);

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();

      // Center
      const bb = geometry.boundingBox;
      const cx = (bb.min.x + bb.max.x) / 2;
      const cy = (bb.min.y + bb.max.y) / 2;
      const cz = (bb.min.z + bb.max.z) / 2;
      geometry.translate(-cx, -cy, -cz);

      const material = new THREE.MeshStandardMaterial({
        color: 0x0a0a0a, metalness: 0.05, roughness: 0.4, flatShading: true,
      });
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      // Ambient / direction lights
      scene.add(new THREE.AmbientLight(0xffffff, 0.55));
      const key = new THREE.DirectionalLight(0xffffff, 1.1);
      key.position.set(1, 2, 1); scene.add(key);
      const rim = new THREE.DirectionalLight(0xd2431a, 0.6);
      rim.position.set(-2, 1, -1); scene.add(rim);

      // Grid
      const gridSize = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z) * 2;
      const grid = new THREE.GridHelper(gridSize, 20, 0xcccccc, 0xeeeeee);
      grid.position.y = -(bb.max.z - bb.min.z) / 2 - 1;
      grid.rotation.x = Math.PI / 2;
      scene.add(grid);

      const r = geometry.boundingSphere.radius;
      camera.position.set(r * 1.4, r * 1.4, r * 1.4);
      camera.lookAt(0, 0, 0);

      // simple orbit via mouse
      let isDown = false, lastX = 0, lastY = 0;
      let phi = Math.PI / 4, theta = Math.PI / 4, radius = r * 2.2;
      const updateCamera = () => {
        camera.position.x = radius * Math.sin(phi) * Math.cos(theta);
        camera.position.y = radius * Math.cos(phi);
        camera.position.z = radius * Math.sin(phi) * Math.sin(theta);
        camera.lookAt(0, 0, 0);
      };
      updateCamera();
      const onDown = (e) => { isDown = true; lastX = e.clientX; lastY = e.clientY; };
      const onUp = () => { isDown = false; };
      const onMove = (e) => {
        if (!isDown) return;
        theta -= (e.clientX - lastX) * 0.01;
        phi -= (e.clientY - lastY) * 0.01;
        phi = Math.max(0.1, Math.min(Math.PI - 0.1, phi));
        lastX = e.clientX; lastY = e.clientY;
        updateCamera();
      };
      const onWheel = (e) => {
        e.preventDefault();
        radius *= 1 + e.deltaY * 0.001;
        radius = Math.max(r * 0.5, Math.min(r * 10, radius));
        updateCamera();
      };
      renderer.domElement.addEventListener("mousedown", onDown);
      window.addEventListener("mouseup", onUp);
      window.addEventListener("mousemove", onMove);
      renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

      // auto-rotate on idle
      let raf;
      const tick = () => {
        if (!isDown) { theta += 0.002; updateCamera(); }
        renderer.render(scene, camera);
        raf = requestAnimationFrame(tick);
      };
      tick();

      // handle resize
      const onResize = () => {
        const w = container.clientWidth;
        camera.aspect = w / height;
        camera.updateProjectionMatrix();
        renderer.setSize(w, height);
      };
      window.addEventListener("resize", onResize);

      return () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("mouseup", onUp);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("resize", onResize);
        renderer.dispose(); geometry.dispose(); material.dispose();
        if (container) container.innerHTML = "";
      };
    } catch (e) {
      setError(e.message || "Erro a carregar STL");
    }
  }, [arrayBuffer, onAnalysis, height]);

  if (error) return <div className="p-6 border border-rose-200 bg-rose-50 text-rose-700 text-sm">{error}</div>;
  return <div ref={containerRef} style={{ width: "100%", height }} className="border border-border bg-secondary" data-testid="stl-viewer" />;
}
